// Operação: Calendários, Políticas de SLA e Áreas.
//
// Calendário ──< Política de SLA ──< Assunto
//      └──────── Área (distribuição, plantão, avisos)
//
// Decisão registrada em §5.5: o relógio de SLA conta pelo calendário DA
// POLÍTICA, não pelo da área. Assim o prazo não muda quando o chamado é
// escalonado para outra área. O calendário da área governa distribuição,
// plantão e os gatilhos de escalonamento que não são de SLA.
import { z } from 'zod';
import { DISTRIBUTION_MODES, HOLIDAY_KINDS, SLA_UNITS } from './enums.js';
/* ── Calendários ─────────────────────────────────────────────────────────── */
export const WEEKDAYS = [
    'Domingo',
    'Segunda-feira',
    'Terça-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sábado',
];
/** Minutos desde a meia-noite. 08:00 = 480. */
export const minuteOfDaySchema = z
    .number()
    .int()
    .min(0)
    .max(24 * 60);
export const workdaySchema = z
    .object({
    weekday: z.number().int().min(0).max(6),
    startsAt: minuteOfDaySchema,
    endsAt: minuteOfDaySchema,
})
    .refine((v) => v.startsAt < v.endsAt, {
    message: 'O fim precisa ser depois do início',
    path: ['endsAt'],
});
export const calendarExceptionSchema = z
    .object({
    date: z.string().date(),
    name: z.string().trim().min(2).max(120),
    kind: z.enum(HOLIDAY_KINDS).default('nacional'),
    /** Ambos nulos = sem atendimento; preenchidos = horário reduzido. */
    startsAt: minuteOfDaySchema.nullable().default(null),
    endsAt: minuteOfDaySchema.nullable().default(null),
})
    .refine((v) => (v.startsAt === null) === (v.endsAt === null), {
    message: 'Informe início e fim, ou deixe os dois em branco para "sem atendimento"',
    path: ['endsAt'],
})
    .refine((v) => v.startsAt === null || v.endsAt === null || v.startsAt < v.endsAt, {
    message: 'O fim precisa ser depois do início',
    path: ['endsAt'],
});
export const calendarInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    timezone: z.string().trim().min(3).max(60).default('America/Sao_Paulo'),
    workdays: z.array(workdaySchema).default([]),
    exceptions: z.array(calendarExceptionSchema).default([]),
    /** Área que recebe chamados de prioridade Alta fora do horário (§5.10).
     *  Esta é a ÚNICA fonte do plantão: não há regra de roteamento paralela. */
    oncallAreaId: z.string().uuid().nullable().default(null),
    active: z.boolean().default(true),
});
export const CALENDAR_SORTABLE = [
    'name',
    'timezone',
    'weeklyMinutes',
    'exceptionCount',
    'active',
    'createdAt',
];
/* ── Políticas de SLA ────────────────────────────────────────────────────── */
export const slaPolicyInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    firstResponseValue: z.number().int().min(1).max(10_000),
    firstResponseUnit: z.enum(SLA_UNITS).default('horas'),
    resolutionValue: z.number().int().min(1).max(10_000),
    resolutionUnit: z.enum(SLA_UNITS).default('horas'),
    calendarId: z.string().uuid(),
    pauseWaitingClient: z.boolean().default(true),
    pauseWaitingThirdParty: z.boolean().default(true),
    alertAtPercent: z.number().int().min(1).max(100).default(80),
    alertAssignee: z.boolean().default(true),
    alertAreaLeader: z.boolean().default(true),
    alertSupportManager: z.boolean().default(false),
    markBreachedOnDue: z.boolean().default(true),
    active: z.boolean().default(true),
});
export const SLA_POLICY_SORTABLE = [
    'name',
    'calendarName',
    'firstResponseValue',
    'resolutionValue',
    'subjectCount',
    'active',
    'createdAt',
];
export const SLA_UNIT_LABEL = {
    minutos: 'minutos',
    horas: 'horas',
    dias_uteis: 'dias úteis',
};
/** "30 min", "4 h", "3 dias úteis". */
export function formatSlaTarget(value, unit) {
    if (unit === 'minutos')
        return `${value} min`;
    if (unit === 'horas')
        return `${value} h`;
    return `${value} ${value === 1 ? 'dia útil' : 'dias úteis'}`;
}
/* ── Áreas ───────────────────────────────────────────────────────────────── */
export const areaMemberSchema = z.object({
    userId: z.string().uuid(),
    role: z.enum(['leader', 'member']).default('member'),
    /** Chamados simultâneos que a pessoa aguenta. */
    capacity: z.number().int().min(1).max(100).default(5),
    availability: z.enum(['disponivel', 'ausente']).default('disponivel'),
    receivesTickets: z.boolean().default(true),
});
export const areaInputSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(2000).optional().default(''),
    calendarId: z.string().uuid(),
    distribution: z.enum(DISTRIBUTION_MODES).default('fila'),
    respectCapacity: z.boolean().default(true),
    skipUnavailable: z.boolean().default(true),
    members: z.array(areaMemberSchema).default([]),
    /** Cadeia de escalonamento depois do líder. O nível 1 é sempre o líder da
     *  própria área, então não entra nesta lista. */
    escalationAreaIds: z.array(z.string().uuid()).default([]),
    active: z.boolean().default(true),
})
    .refine((v) => v.members.filter((m) => m.role === 'leader').length <= 1, {
    message: 'A área só pode ter um líder',
    path: ['members'],
});
export const AREA_SORTABLE = [
    'name',
    'leaderName',
    'memberCount',
    'subjectCount',
    'openTicketCount',
    'distribution',
    'calendarName',
    'active',
    'createdAt',
];
export const DISTRIBUTION_LABEL = {
    fila: 'Fila da área',
    rodizio: 'Rodízio',
    menor_carga: 'Menor carga',
};
export const DISTRIBUTION_HINT = {
    fila: 'Quem estiver livre assume o chamado.',
    rodizio: 'Atribui em sequência aos integrantes disponíveis.',
    menor_carga: 'Atribui a quem tem menos chamados abertos.',
};
